import { current } from '@reduxjs/toolkit';
import { ProjectId, WorkspaceId } from 'core/model';
import {
    addProject,
    archiveProject,
    deleteProject,
    fetchArchivedProjects,
    fetchProjectTasks,
    leaveProject,
    moveProject,
    renameProject,
    setProjectColor,
    unarchiveProject,
} from 'store/projectsThunks';

export const projectsReducers = (builder: any) => {
    builder
        // fetchProjectTasks
        .addCase(fetchProjectTasks.pending, (state: any) => {
            state.status.fetchProjectTasks = 'LOADING';
        })
        .addCase(fetchProjectTasks.fulfilled, (state: any, action: any) => {
            state.status.fetchProjectTasks = 'SUCCEEDED';

            const { projectId, archived, tasks } = action.payload;

            const targetList = archived
                ? state.archivedProjects.projects
                : state.projects;
            const project = targetList.find((p: any) => p.id === projectId);

            if (project) {
                project.tasks = tasks;
            }
        })
        .addCase(fetchProjectTasks.rejected, (state: any, action: any) => {
            state.status.fetchProjectTasks = 'FAILED';
            state.error.fetchProjectTasks =
                action.error?.message || 'An error occurred';
        })
        // fetchArchivedProjects
        .addCase(fetchArchivedProjects.pending, (state: any) => {
            state.status.fetchArchivedProjects = 'LOADING';
            state.archivedProjects.error = null;
        })
        .addCase(fetchArchivedProjects.fulfilled, (state: any, action: any) => {
            state.status.fetchArchivedProjects = 'SUCCEEDED';
            state.archivedProjects.projects = action.payload;
        })
        .addCase(fetchArchivedProjects.rejected, (state: any, action: any) => {
            state.status.fetchArchivedProjects = 'FAILED';
            state.error.fetchArchivedProjects =
                action.error.message || 'An error occurred';
        })
        // addProject
        .addCase(addProject.pending, (state: any) => {
            state.error.addProject = null;
        })
        .addCase(addProject.fulfilled, (state: any, action: any) => {
            const newProject = {
                id: action.payload.projectId as ProjectId,
                workspaceId: action.payload.workspaceId as WorkspaceId,
                projectRef: {
                    projectId: action.payload.projectId as ProjectId,
                    workspaceId: action.payload.workspaceId as WorkspaceId,
                },
                name: action.payload.name,
                color: action.payload.color,
                tasks: [],
                completedTasksCount: 0,
                shared: null,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };

            if (action.meta.arg.asFirst) {
                state.projects.unshift(newProject);
            } else {
                state.projects.push(newProject);
            }
        })
        .addCase(addProject.rejected, (state: any, action: any) => {
            state.error.addProject =
                action.error.message || 'An error occurred';
        })
        // renameProject
        .addCase(renameProject.pending, (state: any) => {
            state.error.renameProject = null;
        })
        .addCase(renameProject.fulfilled, (state: any, action: any) => {
            state.projects
                .filter((p: any) => p.id === action.payload.request.projectId)
                .forEach(
                    (p: any) => (p.name = action.payload.request.command.name)
                );
        })
        .addCase(renameProject.rejected, (state: any, action: any) => {
            state.error.renameProject =
                action.error.message || 'An error occurred';
        })
        // setProjectColor
        .addCase(setProjectColor.pending, (state: any) => {
            state.error.setProjectColor = null;
        })
        .addCase(setProjectColor.fulfilled, (state: any, action: any) => {
            state.projects
                .filter((p: any) => p.id === action.payload.request.projectId)
                .forEach(
                    (p: any) => (p.color = action.payload.request.command.color)
                );
        })
        .addCase(setProjectColor.rejected, (state: any, action: any) => {
            state.error.setProjectColor =
                action.error.message || 'An error occurred';
        })
        // moveProject
        .addCase(moveProject.pending, (state: any) => {
            state.error.moveProject = null;
        })
        .addCase(moveProject.fulfilled, (state: any, action: any) => {
            let projectsNewOrder = [...current(state.projects)];
            let item = projectsNewOrder[action.payload.itemIndex];
            projectsNewOrder.splice(action.payload.itemIndex, 1);

            state.projects =
                action.payload.itemIndex < 0
                    ? projectsNewOrder
                    : [
                          ...projectsNewOrder.slice(0, action.payload.newIndex),
                          item,
                          ...projectsNewOrder.slice(action.payload.newIndex),
                      ];
        })
        .addCase(moveProject.rejected, (state: any, action: any) => {
            state.error.moveProject =
                action.error.message || 'An error occurred';
        })
        // archiveProject
        .addCase(archiveProject.pending, (state: any, action: any) => {
            state.error.archiveProject = null;
        })
        .addCase(archiveProject.fulfilled, (state: any, action: any) => {
            const archivedProject = state.projects.find(
                (project: any) => project.id === action.payload
            );
            state.projects = state.projects.filter(
                (project: any) => project.id !== action.payload
            );
            if (archivedProject) {
                state.archivedProjects.projects.push({
                    id: archivedProject.id,
                    name: archivedProject.name,
                    color: archivedProject.color,
                    workspaceId: archivedProject.workspaceId,
                });
            }
            state.archivedProjectsCount = state.archivedProjectsCount + 1;
        })
        .addCase(archiveProject.rejected, (state: any, action: any) => {
            state.error.archiveProject =
                action.error.message || 'An error occurred';
        })
        // unarchiveProject
        .addCase(unarchiveProject.pending, (state: any) => {
            state.error.unarchiveProject = null;
        })
        .addCase(unarchiveProject.fulfilled, (state: any, action: any) => {
            state.archivedProjects.projects =
                state.archivedProjects.projects.filter(
                    (project: any) => project.id !== action.payload.id
                );

            // Ensure the unarchived project has all required properties
            // Reset all non-completed task states to IDLE since they won't be in plannedTasks
            const sanitizedTasks = (action.payload.tasks || []).map(
                (task: any) => ({
                    ...task,
                    state: task.completedAt ? task.state : 'IDLE',
                })
            );

            // Use current workspace ID since API might not return it
            const currentWorkspaceId = state.id as WorkspaceId;

            const unarchivedProject = {
                id: action.payload.id as ProjectId,
                workspaceId: currentWorkspaceId,
                projectRef: {
                    projectId: action.payload.id as ProjectId,
                    workspaceId: currentWorkspaceId,
                },
                name: action.payload.name,
                color: action.payload.color,
                tasks: sanitizedTasks,
                completedTasksCount: action.payload.completedTasksCount || 0,
                shared: action.payload.shared || null,
                createdAt: action.payload.createdAt,
                updatedAt: action.payload.updatedAt,
            };

            state.projects.push(unarchivedProject);
            state.archivedProjectsCount = state.archivedProjectsCount - 1;
        })
        .addCase(unarchiveProject.rejected, (state: any, action: any) => {
            state.error.unarchiveProject =
                action.error.message || 'An error occurred';
        })
        // deleteProject
        .addCase(deleteProject.pending, (state: any) => {
            state.error.deleteProject = null;
        })
        .addCase(deleteProject.fulfilled, (state: any, action: any) => {
            state.projects = state.projects.filter(
                (project: any) => project.id !== action.payload
            );
        })
        .addCase(deleteProject.rejected, (state: any, action: any) => {
            state.error.deleteProject =
                action.error.message || 'An error occurred';
        })
        // leaveProject
        .addCase(leaveProject.pending, (state: any) => {
            state.error.leaveProject = null;
        })
        .addCase(leaveProject.fulfilled, (state: any, action: any) => {
            state.projects = state.projects.filter(
                (project: any) => project.id !== action.payload
            );
        })
        .addCase(leaveProject.rejected, (state: any, action: any) => {
            state.error.leaveProject =
                action.error.message || 'An error occurred';
        });
};
